import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Card, CardHeader, CardContent } from '../Common/Card';

const ApiLogsViewer = ({ executionId, onClose }) => {
  const [apiCalls, setApiCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [executionSummary, setExecutionSummary] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    request: false,
    response: false,
    headers: false,
    toolCalls: true  // Auto-expand tool calls section when present
  });

  useEffect(() => {
    if (executionId) {
      fetchApiCalls();
    }
  }, [executionId]);

  const fetchApiCalls = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('sankalvax_token');
      const urlParams = new URLSearchParams(window.location.search);
      const isV2Execution = urlParams.get('type') === 'v2';

      if (isV2Execution) {
        // Use V2 execution details endpoint
        const response = await fetch(`/api/v2/workflows/${executionId}/execution-details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch V2 execution details: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success) {
          // Transform V2 data structure to match existing UI
          const transformedApiCalls = [];

          data.execution_details.forEach(step => {
            // Check step_type instead of api_calls to correctly identify UI vs AI steps
            if (step.step_type === 'ui_step') {
              // UI Step: Show the step with its input/output data
              transformedApiCalls.push({
                id: step.step_execution_id,
                step_execution_id: step.step_execution_id,
                step_name: step.step_name,
                step_type: step.step_type,
                step_status: step.status,
                step_duration_ms: step.execution_time_ms,
                step_started_at: step.started_at,
                step_completed_at: step.completed_at,
                provider: null,
                model: null,
                request_url: null,
                request_body: step.input_data,  // Show input data for UI steps
                response_body: step.output_data, // Show output data for UI steps
                response_status: step.status === 'completed' ? 200 : step.status === 'failed' ? 500 : null,
                duration_ms: step.execution_time_ms,
                tokens_used: step.tokens_used,
                cost: step.cost,
                request_timestamp: step.started_at,
                response_timestamp: step.completed_at,
                error_message: step.error_message,
                is_ui_step: true  // Flag to identify UI steps in rendering
              });
            } else if (step.api_calls && step.api_calls.length > 0) {
              // AI Step with API calls: Show each API call
              step.api_calls.forEach(call => {
                transformedApiCalls.push({
                  id: call.id,
                  step_execution_id: step.step_execution_id,
                  step_name: step.step_name,
                  step_type: step.step_type,
                  step_status: step.status,
                  step_duration_ms: step.execution_time_ms,
                  step_started_at: step.started_at,
                  step_completed_at: step.completed_at,
                  provider: call.provider,
                  model: call.model,
                  request_url: call.request_url,
                  request_body: call.request_body,
                  request_headers: call.request_headers,
                  response_body: call.response_body,
                  response_headers: call.response_headers,
                  response_status: call.response_status,
                  duration_ms: call.duration_ms,
                  tokens_used: call.tokens_used,
                  cost: call.cost,
                  request_timestamp: call.request_timestamp,
                  response_timestamp: call.response_timestamp,
                  error_message: step.error_message,
                  is_ui_step: false
                });
              });
            } else {
              // AI Step without API calls (failed before making API call)
              transformedApiCalls.push({
                id: step.step_execution_id,
                step_execution_id: step.step_execution_id,
                step_name: step.step_name,
                step_type: step.step_type,
                step_status: step.status,
                step_duration_ms: step.execution_time_ms,
                step_started_at: step.started_at,
                step_completed_at: step.completed_at,
                provider: 'N/A',
                model: 'No API calls made',
                request_url: null,
                request_body: step.input_data,
                response_body: step.output_data,
                response_status: step.status === 'completed' ? 200 : step.status === 'failed' ? 500 : null,
                duration_ms: step.execution_time_ms,
                tokens_used: step.tokens_used,
                cost: step.cost,
                request_timestamp: step.started_at,
                response_timestamp: step.completed_at,
                error_message: step.error_message,
                is_ui_step: false
              });
            }
          });

          setApiCalls(transformedApiCalls);
          setExecutionSummary({
            total_steps: data.total_steps,
            total_api_calls: data.total_api_calls,
            run_id: data.run_id,
            isV2: true
          });
          setError(null);

          // Auto-select first call if available
          if (transformedApiCalls.length > 0) {
            setSelectedCall(transformedApiCalls[0]);
          }
        }
      } else {
        // Keep existing V1 logic
        const response = await fetch(`/api/chat/executions/${executionId}/api-calls`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch execution details: ${response.statusText}`);
        }

        const data = await response.json();
        setApiCalls(data.api_calls || []);
        setExecutionSummary({ isV2: false });
        setError(null);

        if (data.api_calls && data.api_calls.length > 0) {
          setSelectedCall(data.api_calls[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching execution details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    return `${ms}ms`;
  };

  const formatJson = (obj, maxDepth = 3, currentDepth = 0) => {
    if (currentDepth > maxDepth) return '...';
    return JSON.stringify(obj, null, 2);
  };

  // Helper function to extract parameters from assistant message content
  const extractParametersFromContent = (content, toolName) => {
    if (!content) return null;

    // Look for pattern: Executing: toolName\nParameters:\n{...}
    const paramMatch = content.match(new RegExp(`Executing:\\s*${toolName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\nParameters:\\s*\\n({[\\s\\S]*?})(?=\\n|$)`, 'i'));
    if (paramMatch && paramMatch[1]) {
      try {
        // Clean up escaped newlines and quotes, then parse
        const cleanedParams = paramMatch[1]
          .replace(/\\n/g, '\n')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
        return JSON.parse(cleanedParams);
      } catch (e) {
        console.warn('Failed to parse parameters from content:', e);
        return null;
      }
    }
    return null;
  };

  const extractToolCalls = (call) => {
    const toolCalls = [];

    // Debug log to help identify tool call extraction issues
    console.log('🔍 Extracting tool calls from:', call.id, call);

    // Enhanced debugging for tool call issues
    if (call.request_body && call.request_body.messages) {
      call.request_body.messages.forEach((message, msgIndex) => {
        if (message.role === 'assistant' && message.tool_calls) {
          console.log(`🔧 Found assistant message ${msgIndex} with tool_calls:`, message.tool_calls);
          message.tool_calls.forEach((toolCall, tcIndex) => {
            console.log(`   Tool call ${tcIndex}:`, {
              id: toolCall.id,
              name: toolCall.function?.name,
              args: toolCall.function?.arguments,
              type: typeof toolCall.function?.arguments
            });
          });
        }
      });
    }

    // Also check response body for tool calls
    if (call.response_body && call.response_body.choices) {
      const message = call.response_body.choices[0]?.message;
      if (message && message.tool_calls) {
        console.log(`🔧 Found response message with tool_calls:`, message.tool_calls);
        message.tool_calls.forEach((toolCall, tcIndex) => {
          console.log(`   Response tool call ${tcIndex}:`, {
            id: toolCall.id,
            name: toolCall.function?.name,
            args: toolCall.function?.arguments,
            type: typeof toolCall.function?.arguments
          });
        });
      }
    }

    // Extract from request body (tool_calls sent to AI)
    if (call.request_body && call.request_body.messages) {
      const messages = call.request_body.messages;
      messages.forEach((message, msgIndex) => {
        if (message.role === 'assistant' && message.tool_calls) {
          message.tool_calls.forEach((toolCall) => {
            // Parse arguments properly - handle both string and object formats
            let parsedArgs = {};
            try {
              console.log(`🔧 DEBUG: Processing tool call ${toolCall.id}:`, {
                name: toolCall.function?.name,
                arguments: toolCall.function?.arguments,
                type: typeof toolCall.function?.arguments
              });

              if (toolCall.function?.arguments) {
                // OpenAI returns arguments as JSON string
                parsedArgs = typeof toolCall.function.arguments === 'string'
                  ? JSON.parse(toolCall.function.arguments)
                  : toolCall.function.arguments;
              } else if (toolCall.arguments) {
                parsedArgs = toolCall.arguments;
              }

              console.log(`🔧 DEBUG: Parsed args for ${toolCall.function?.name}:`, parsedArgs, 'Keys:', Object.keys(parsedArgs));

              // For legacy tool calls with empty arguments, try to extract from message content
              if (Object.keys(parsedArgs).length === 0) {
                const toolName = toolCall.function?.name || toolCall.name;
                console.log(`⚠️ LEGACY TOOL CALL DETECTED: ${toolName} with empty arguments - searching content`);

                // Try to extract parameters from assistant message content
                const extractedParams = extractParametersFromContent(message.content, toolName);
                if (extractedParams) {
                  parsedArgs = extractedParams;
                  console.log(`✅ EXTRACTED PARAMETERS from content for ${toolName}:`, parsedArgs);
                } else {
                  // Fallback to placeholder if extraction fails
                  parsedArgs = {
                    "__legacy_note": "Parameters not available in API logs",
                    "__tool_name": toolName,
                    "__hint": "Check chat messages for actual parameters used"
                  };
                  console.log(`⚠️ FALLBACK: Using placeholder for ${toolName}:`, parsedArgs);
                }
              }
            } catch (e) {
              console.warn('Failed to parse tool arguments:', e);
              parsedArgs = { error: 'Failed to parse arguments', raw: toolCall.function?.arguments || toolCall.arguments };
            }

            toolCalls.push({
              type: 'outgoing',
              id: toolCall.id,
              name: toolCall.function?.name || toolCall.name,
              arguments: JSON.stringify(parsedArgs, null, 2),
              messageIndex: msgIndex,
              source: 'request'
            });
          });
        }

        // Tool results in messages
        if (message.role === 'tool') {
          console.log(`🔍 Found tool result with ID: ${message.tool_call_id}`);
          toolCalls.push({
            type: 'result',
            id: message.tool_call_id,
            name: 'Tool Result',
            content: message.content,
            messageIndex: msgIndex,
            source: 'request'
          });
        }
      });
    }

    // Extract from response body (AI requesting tool calls)
    if (call.response_body && call.response_body.choices) {
      const message = call.response_body.choices[0]?.message;
      if (message && message.tool_calls) {
        message.tool_calls.forEach((toolCall) => {
          // Parse arguments properly - handle both string and object formats
          let parsedArgs = {};
          try {
            if (toolCall.function?.arguments) {
              // OpenAI returns arguments as JSON string
              parsedArgs = typeof toolCall.function.arguments === 'string'
                ? JSON.parse(toolCall.function.arguments)
                : toolCall.function.arguments;
            } else if (toolCall.arguments) {
              parsedArgs = toolCall.arguments;
            }

            // For legacy tool calls with empty arguments, show a placeholder
            if (Object.keys(parsedArgs).length === 0) {
              const toolName = toolCall.function?.name || toolCall.name;
              console.log(`⚠️ Legacy response tool call detected: ${toolName} with empty arguments`);

              // Set a placeholder indicating this is a legacy tool call
              parsedArgs = {
                "__legacy_note": "Parameters not available in API logs",
                "__tool_name": toolName,
                "__hint": "Check chat messages for actual parameters used"
              };
            }
          } catch (e) {
            console.warn('Failed to parse tool arguments:', e);
            parsedArgs = { error: 'Failed to parse arguments', raw: toolCall.function?.arguments || toolCall.arguments };
          }

          toolCalls.push({
            type: 'requested',
            id: toolCall.id,
            name: toolCall.function?.name || toolCall.name,
            arguments: JSON.stringify(parsedArgs, null, 2),
            source: 'response'
          });
        });
      }
    }

    // Skip AI response extraction - focus only on actual tool results

    console.log('🔧 Found tool calls:', toolCalls.length, toolCalls);
    return toolCalls;
  };

  const deduplicateToolCalls = (toolCalls) => {
    console.log('🔍 DEDUP: Starting deduplication with', toolCalls.length, 'tool calls');

    // First, let's log all tool calls to understand what we're working with
    toolCalls.forEach((call, index) => {
      console.log(`🔍 DEDUP: Tool call ${index}:`, {
        id: call.id,
        name: call.name,
        type: call.type,
        hasParams: (() => {
          try {
            const args = JSON.parse(call.arguments || '{}');
            return Object.keys(args).length > 0 && !args.__legacy_note;
          } catch (e) { return false; }
        })()
      });
    });

    // Group tool calls by ID and name combination
    // Tool results should NOT be grouped together - each belongs to a specific tool call
    const groupedCalls = {};

    toolCalls.forEach((toolCall, index) => {
      // For tool results, use the tool call ID as the key to keep them separate
      // For tool calls, use the tool name to deduplicate duplicates of the same tool
      const key = toolCall.type === 'result'
        ? `result_${toolCall.id}` // Each tool result gets its own group
        : `call_${toolCall.name}`; // Tool calls grouped by name for deduplication

      if (!groupedCalls[key]) {
        groupedCalls[key] = [];
      }

      groupedCalls[key].push({ ...toolCall, originalIndex: index });
    });

    // For each group, prefer calls with actual parameters over empty ones
    const deduplicatedCalls = [];

    Object.keys(groupedCalls).forEach(key => {
      const group = groupedCalls[key];
      console.log(`🔧 DEDUP: Processing group ${key} with ${group.length} calls`);

      if (group.length === 1) {
        // Only one call, keep it
        deduplicatedCalls.push(group[0]);
        console.log(`✅ DEDUP: Keeping single call for ${key}`);
      } else {
        // Handle tool results differently - they should always be kept since each is unique
        if (key.startsWith('result_')) {
          // For tool results, keep all of them since they're already separated by ID
          group.forEach(call => {
            deduplicatedCalls.push(call);
            console.log(`✅ DEDUP: Keeping tool result for ${key}:`, call.id);
          });
        } else {
          // Multiple tool calls - prefer ones with parameters
          const callsWithParams = group.filter(call => {
            try {
              const args = JSON.parse(call.arguments || '{}');
              const hasParams = Object.keys(args).length > 0 && !args.__legacy_note;
              console.log(`🔍 DEDUP: Call ${call.id} has params:`, hasParams, 'Keys:', Object.keys(args));
              return hasParams;
            } catch (e) {
              return false;
            }
          });

          const callsWithoutParams = group.filter(call => {
            try {
              const args = JSON.parse(call.arguments || '{}');
              const hasParams = Object.keys(args).length > 0 && !args.__legacy_note;
              return !hasParams;
            } catch (e) {
              return true; // Treat parse errors as no params
            }
          });

          if (callsWithParams.length > 0) {
            // Among calls with params, prefer "requested" over "outgoing"
            const requestedCalls = callsWithParams.filter(call => call.type === 'requested');
            const selectedCall = requestedCalls.length > 0 ? requestedCalls[0] : callsWithParams[0];
            deduplicatedCalls.push(selectedCall);
            console.log(`✅ DEDUP: Kept call with params for ${key}:`, selectedCall.id, 'Type:', selectedCall.type);
          } else if (callsWithoutParams.length > 0) {
            // Among calls without params, prefer "requested" over "outgoing"
            const requestedCalls = callsWithoutParams.filter(call => call.type === 'requested');
            const selectedCall = requestedCalls.length > 0 ? requestedCalls[0] : callsWithoutParams[0];
            deduplicatedCalls.push(selectedCall);
            console.log(`⚠️ DEDUP: Kept call without params for ${key}:`, selectedCall.id, 'Type:', selectedCall.type);
          }
        }
      }
    });

    // Sort by original index to maintain order
    deduplicatedCalls.sort((a, b) => a.originalIndex - b.originalIndex);

    console.log(`🎯 DEDUP: Reduced from ${toolCalls.length} to ${deduplicatedCalls.length} tool calls`);
    return deduplicatedCalls;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-600';
    if (status >= 400 && status < 500) return 'text-red-600';
    if (status >= 500) return 'text-red-800';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading API call logs...</div>
      </div>
    );
  }

  const renderExecutionSummary = () => {
    if (!executionSummary?.isV2) return null;

    return (
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="text-lg font-semibold mb-3 text-blue-800 dark:text-blue-200">V2 Execution Summary</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Total Steps:</span>
            <span className="ml-2 font-mono font-semibold text-blue-700 dark:text-blue-300">{executionSummary?.total_steps || 0}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">API Calls:</span>
            <span className="ml-2 font-mono font-semibold text-blue-700 dark:text-blue-300">{executionSummary?.total_api_calls || 0}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Run ID:</span>
            <span className="ml-2 font-mono text-xs bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded">{executionSummary?.run_id?.substring(0, 8)}...</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium">{executionSummary?.isV2 ? 'V2 Run ID:' : 'Execution ID:'}</span>
          <span className="ml-2 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
            {executionId?.substring(0, 8)}...
          </span>
          {executionSummary?.isV2 && (
            <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs rounded-full font-medium">
              V2 Engine
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
        >
          ← Back to Executions
        </button>
      </div>

      {/* V2 Execution Summary */}
      {renderExecutionSummary()}

      {/* Error Display */}
      {error && (
        <Card>
          <CardContent>
            <div className="text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="font-medium">Error loading API calls:</div>
              <div className="mt-1 text-sm">{error}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {apiCalls.length === 0 ? (
        <Card>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              No API calls found for this execution
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Execution Steps List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <h3 className="font-medium">Execution Steps ({apiCalls.length})</h3>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-gray-200">
                  {apiCalls.map((call, index) => (
                    <div
                      key={call.id}
                      onClick={() => setSelectedCall(call)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 ${
                        selectedCall?.id === call.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          #{index + 1} - {call.step_name}
                        </span>
                        <span className={`text-sm font-mono ${getStatusColor(call.response_status)}`}>
                          {call.response_status || 'Error'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        {call.is_ui_step ? (
                          <div className="flex items-center space-x-1">
                            <span className="text-blue-600 dark:text-blue-400">🖥️</span>
                            <span className="text-blue-600 dark:text-blue-400 font-medium">UI Step</span>
                          </div>
                        ) : (
                          <div>{call.provider} - {call.model}</div>
                        )}
                        <div>{formatDuration(call.duration_ms)}</div>
                        {!call.is_ui_step && <div>{call.tokens_used || 0} tokens</div>}
                        {!call.is_ui_step && (() => {
                          const toolCallsCount = deduplicateToolCalls(extractToolCalls(call)).length;
                          return toolCallsCount > 0 ? (
                            <div className="flex items-center space-x-1">
                              <span className="text-purple-600 dark:text-purple-400">🔧</span>
                              <span className="text-purple-600 dark:text-purple-400 font-medium">
                                {toolCallsCount} tool call{toolCallsCount !== 1 ? 's' : ''}
                              </span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                      {call.error_message && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                          Error: {call.error_message.substring(0, 50)}...
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Selected Call Details */}
          <div className="lg:col-span-2">
            {selectedCall ? (
              <div className="space-y-4">
                {/* Step Information (V2 only) */}
                {executionSummary?.isV2 && selectedCall.step_execution_id && (
                  <Card>
                    <CardHeader>
                      <h3 className="font-medium text-blue-700 dark:text-blue-300">Step Information</h3>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Step Name:</label>
                            <div className="text-blue-800 dark:text-blue-200 font-mono text-sm bg-white dark:bg-gray-800 px-2 py-1 rounded mt-1">
                              {selectedCall.step_name}
                            </div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Step Type:</label>
                            <div className="text-blue-800 dark:text-blue-200 font-mono">{selectedCall.step_type}</div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Step Status:</label>
                            <div className={`font-mono font-semibold ${
                              selectedCall.step_status === 'completed' ? 'text-green-600 dark:text-green-400' :
                              selectedCall.step_status === 'running' ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-red-600 dark:text-red-400'
                            }`}>
                              {selectedCall.step_status}
                            </div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Step Duration:</label>
                            <div className="text-blue-800 dark:text-blue-200 font-mono">{formatDuration(selectedCall.step_duration_ms)}</div>
                          </div>
                          {selectedCall.step_started_at && (
                            <div className="md:col-span-2">
                              <label className="font-medium text-gray-700 dark:text-gray-300">Step Timeline:</label>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                Started: {formatTimestamp(selectedCall.step_started_at)}
                                {selectedCall.step_completed_at && (
                                  <span className="ml-4">Completed: {formatTimestamp(selectedCall.step_completed_at)}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Call Overview */}
                <Card>
                  <CardHeader>
                    <h3 className="font-medium">
                      {selectedCall.is_ui_step ? '🖥️ UI Step Details' : 'API Call Details'}
                    </h3>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {!selectedCall.is_ui_step && (
                        <>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Provider:</label>
                            <div className="text-gray-900 dark:text-gray-100">{selectedCall.provider}</div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Model:</label>
                            <div className="text-gray-900 dark:text-gray-100">{selectedCall.model}</div>
                          </div>
                        </>
                      )}
                      {selectedCall.is_ui_step && (
                        <div className="md:col-span-2">
                          <label className="font-medium text-gray-700 dark:text-gray-300">Step Type:</label>
                          <div className="text-blue-600 dark:text-blue-400 font-medium">
                            🖥️ User Interface Step
                          </div>
                        </div>
                      )}
                      {!executionSummary?.isV2 && (
                        <div>
                          <label className="font-medium text-gray-700 dark:text-gray-300">Step:</label>
                          <div className="text-gray-900 dark:text-gray-100 font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                            {selectedCall.step_name}
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="font-medium text-gray-700 dark:text-gray-300">Status:</label>
                        <div className={`font-mono ${getStatusColor(selectedCall.response_status)}`}>
                          {selectedCall.response_status || 'Error'}
                        </div>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700 dark:text-gray-300">Duration:</label>
                        <div className="text-gray-900 dark:text-gray-100">{formatDuration(selectedCall.duration_ms)}</div>
                      </div>
                      {!selectedCall.is_ui_step && (
                        <>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Tokens:</label>
                            <div className="text-gray-900 dark:text-gray-100">{selectedCall.tokens_used || 0}</div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Cost:</label>
                            <div className="text-gray-900 dark:text-gray-100">${(selectedCall.cost || 0).toFixed(6)}</div>
                          </div>
                        </>
                      )}
                      <div>
                        <label className="font-medium text-gray-700 dark:text-gray-300">
                          {selectedCall.is_ui_step ? 'Started At:' : 'Request Time:'}
                        </label>
                        <div className="text-gray-900 dark:text-gray-100 text-xs">
                          {formatTimestamp(selectedCall.request_timestamp)}
                        </div>
                      </div>
                    </div>
                    {!selectedCall.is_ui_step && selectedCall.request_url && (
                      <div className="mt-4">
                        <label className="font-medium text-gray-700 dark:text-gray-300">URL:</label>
                        <div className="text-gray-900 dark:text-gray-100 font-mono text-xs bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded mt-1 break-all">
                          {selectedCall.request_url}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Tool Calls Section - Only for AI steps */}
                {!selectedCall.is_ui_step && (() => {
                  const rawToolCalls = extractToolCalls(selectedCall);
                  const toolCalls = deduplicateToolCalls(rawToolCalls);
                  if (toolCalls.length === 0) return null;

                  return (
                    <Card>
                      <CardHeader>
                        <button
                          onClick={() => toggleSection('toolCalls')}
                          className="w-full text-left flex justify-between items-center"
                        >
                          <h3 className="font-medium text-purple-700 dark:text-purple-300">
                            🔧 Tool Calls ({toolCalls.length})
                          </h3>
                          <span>{expandedSections.toolCalls ? '▼' : '▶'}</span>
                        </button>
                      </CardHeader>
                      {expandedSections.toolCalls && (
                        <CardContent>
                          <div className="space-y-4">
                            {toolCalls.map((toolCall, index) => (
                              <div key={`${toolCall.id}-${index}`} className="border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                      toolCall.type === 'requested'
                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                        : toolCall.type === 'outgoing'
                                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    }`}>
                                      {toolCall.type === 'requested' ? '📤 AI Requested' :
                                       toolCall.type === 'outgoing' ? '🔄 Executing' : '✅ Result'}
                                    </span>
                                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                      🔧 {toolCall.name || 'Unknown Tool'}
                                    </span>
                                  </div>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                    ID: {toolCall.id}
                                  </span>
                                </div>

                                {toolCall.arguments && (
                                  <div className="mb-4">
                                    <label className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2 block">
                                      📥 Request Parameters:
                                    </label>
                                    <pre className="text-sm bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border overflow-x-auto font-mono">
                                      {typeof toolCall.arguments === 'string'
                                        ? toolCall.arguments
                                        : JSON.stringify(toolCall.arguments, null, 2)
                                      }
                                    </pre>
                                  </div>
                                )}

                                {toolCall.content && (
                                  <div>
                                    <label className="text-sm font-semibold mb-2 block text-green-700 dark:text-green-300">
                                      📤 Tool Response:
                                    </label>
                                    <pre className="text-sm p-4 rounded-lg border overflow-x-auto font-mono bg-green-50 dark:bg-green-900/20">
                                      {typeof toolCall.content === 'string'
                                        ? toolCall.content
                                        : JSON.stringify(toolCall.content, null, 2)
                                      }
                                    </pre>
                                  </div>
                                )}

                                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                  Source: {toolCall.source} {toolCall.messageIndex !== undefined && `(message ${toolCall.messageIndex})`}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })()}

                {/* Request Details / Input Data */}
                <Card>
                  <CardHeader>
                    <button
                      onClick={() => toggleSection('request')}
                      className="w-full text-left flex justify-between items-center"
                    >
                      <h3 className="font-medium">
                        {selectedCall.is_ui_step ? '📥 Input Data' : 'Request Body'}
                      </h3>
                      <span>{expandedSections.request ? '▼' : '▶'}</span>
                    </button>
                  </CardHeader>
                  {expandedSections.request && (
                    <CardContent>
                      <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-x-auto border">
                        {formatJson(selectedCall.request_body)}
                      </pre>
                    </CardContent>
                  )}
                </Card>

                {/* Response Details / Output Data */}
                <Card>
                  <CardHeader>
                    <button
                      onClick={() => toggleSection('response')}
                      className="w-full text-left flex justify-between items-center"
                    >
                      <h3 className="font-medium">
                        {selectedCall.is_ui_step ? '📤 Output Data' : 'Response Body'}
                      </h3>
                      <span>{expandedSections.response ? '▼' : '▶'}</span>
                    </button>
                  </CardHeader>
                  {expandedSections.response && (
                    <CardContent>
                      {selectedCall.error_message ? (
                        <div className="text-red-600 bg-red-50 dark:bg-red-900/20 p-4 rounded border border-red-200 dark:border-red-800 font-mono text-sm">
                          {selectedCall.error_message}
                        </div>
                      ) : (
                        <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-x-auto border">
                          {formatJson(selectedCall.response_body)}
                        </pre>
                      )}
                    </CardContent>
                  )}
                </Card>

                {/* Headers - Only for AI steps */}
                {!selectedCall.is_ui_step && (
                  <Card>
                    <CardHeader>
                      <button
                        onClick={() => toggleSection('headers')}
                        className="w-full text-left flex justify-between items-center"
                      >
                        <h3 className="font-medium">Headers</h3>
                        <span>{expandedSections.headers ? '▼' : '▶'}</span>
                      </button>
                    </CardHeader>
                    {expandedSections.headers && (
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Request Headers:</h4>
                            <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded overflow-x-auto border">
                              {formatJson(selectedCall.request_headers)}
                            </pre>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Response Headers:</h4>
                            <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded overflow-x-auto border">
                              {formatJson(selectedCall.response_headers)}
                            </pre>
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent>
                  <div className="text-center py-8 text-gray-500">
                    Select an API call from the list to view details
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiLogsViewer;